import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';

// Eliminamos la importación del video
// import heroVideo from '../../assets/videos/home/agt-hero.mp4';
import heroBgImage from '../../assets/images/home/hero-bg.png';
import consciousAIImage from '../../assets/images/home/conscious-ai.png';
import agtUpsImage from '../../assets/images/home/agt-ups.png';
import agtEduImage from '../../assets/images/home/agt-edu.png';
import purposeImage from '../../assets/images/home/purpose.png';
import allInOneImage from '../../assets/images/home/all-in-one.png';
import searchProImage from '../../assets/images/home/search-pro.png';
import makerProImage from '../../assets/images/home/maker-pro.png';
import videoImage from '../../assets/images/home/video.png';
import academicImage from '../../assets/images/home/academic.png';
import growthImage from '../../assets/images/home/growth.png';
import productImage from '../../assets/images/home/product.png';
import cashFlowImage from '../../assets/images/home/cash-flow.png';
import councilImage from '../../assets/images/home/council.png';

const ButtonStyle = ({children, variant = 'light', onClick, to, isExternal}) => {
  const buttonClasses = `
    px-5 py-2.5 rounded-full
    ${variant === 'light' ? 'bg-white text-[#1d1d1f]' : 'bg-[#1d1d1f] text-white'}
    ${variant === 'light' ? 'hover:bg-white/90' : 'hover:bg-[#1d1d1f]/90'}
    transition-all duration-300
    text-[17px] font-medium
  `;

  if (isExternal) {
    return (
      <a 
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClasses}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link 
      to={to}
      className={buttonClasses}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

const StoryModal = ({ isOpen, onClose, content }) => {
  const paragraphs = content?.description?.split('\n\n') || [];

  return (
    <AnimatePresence mode="wait">
      {isOpen && content && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 sm:p-6"
          style={{ backdropFilter: 'blur(8px)' }}
        >
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/20" 
            onClick={onClose} 
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ 
              duration: 0.2,
              ease: [0.4, 0, 0.2, 1]
            }}
            className="relative w-full max-w-4xl mx-auto my-2 sm:my-8 bg-white rounded-[20px] sm:rounded-[32px] shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-5 sm:p-8 md:p-12">
              <button 
                onClick={onClose}
                className="absolute right-3 top-3 sm:right-6 sm:top-6 text-[#1d1d1f] hover:text-[#6e6e73] transition-colors p-2 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>

              <div className="max-w-3xl">
                <div className="inline-block px-2 py-1 sm:px-3 sm:py-1 mb-3 sm:mb-4 text-xs sm:text-sm font-medium text-[#6e6e73] bg-gray-100 rounded-full">
                  What's new
                </div>

                <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-[#1d1d1f] mb-4 sm:mb-8 leading-tight">
                  {content.title}
                </h2>

                <div className="space-y-3 sm:space-y-6 prose prose-sm sm:prose-lg max-w-none">
                  {paragraphs.map((paragraph, index) => (
                    <p key={index} className="text-base sm:text-lg leading-relaxed text-[#424245]">
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>

                <div className="mt-6 sm:mt-10 pt-4 sm:pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="text-xs sm:text-sm text-[#6e6e73]">
                    Available with AGT subscription
                  </div>
                  <button className="flex items-center gap-1 sm:gap-2 text-base sm:text-lg font-medium text-blue-600 hover:text-blue-700 transition-colors group">
                    Learn more
                    <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Reemplazamos el componente de video con una imagen estática
const HeroBackground = () => {
  return (
    <div className="relative w-full h-[580px] sm:h-[692px] overflow-hidden">
      <img
        src={heroBgImage}
        alt="AGT Background"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 flex flex-col items-center justify-between pb-12 pt-[48px] px-6 sm:px-8 md:px-0">
        <div className="text-center">
          <h2 className="text-[32px] sm:text-[48px] md:text-[56px] font-semibold text-white">AGT</h2>
          <p className="text-[19px] sm:text-[24px] md:text-[28px] mt-[6px] text-white">Answers. Solutions. Results.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mt-6">
            <ButtonStyle to="/agt">Learn more</ButtonStyle>
            <ButtonStyle to="https://agtapp.com/" variant="dark" isExternal>Try now</ButtonStyle>
          </div>
        </div>
        {}
      </div>
    </div>
  );
};

const DirectiveAIHome = () => {
  const scrollContainerRef = useRef(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: window.innerWidth < 640 ? -280 : -425,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: window.innerWidth < 640 ? 280 : 425,
        behavior: 'smooth'
      });
    }
  };

  const productCards = [
    {
      title: "AGT Search Pro",
      subtitle: "See beyond words",
      backgroundColor: "#4A90E2",
      category: "AGT PRO", // Cambiado a mayúsculas
      image: searchProImage,
      modalContent: {
        title: "AGT Search Pro",
        description: `Experience a revolutionary leap in search technology with AGT Search Pro. Blend multi-image recognition with real-time data analysis for unprecedented search capabilities.

        Advanced algorithms understand context and predict needs, delivering results beyond simple keyword matching. With interactive map integration and real-time updates, you're always working with the latest information.

        Welcome to the future of search, where technology amplifies human potential.`
      }
    },
    {
      title: "AGT Maker Pro",
      subtitle: "Create the extraordinary",
      backgroundColor: "#50E3C2",
      category: "AGT PRO", // Cambiado a mayúsculas
      image: makerProImage,
      modalContent: {
        title: "AGT Maker Pro",
        description: `Transform your ideas into reality with AGT Maker Pro. Our intelligent design system turns presentations into dynamic narratives that captivate and inspire.

        Create fluid animations and comprehensive reports that adapt to your brand's voice. Every feature removes technical barriers, letting your creativity flow uninterrupted.

        Step into a new era of creative expression where your vision meets limitless potential.`
      }
    },
    {
      title: "AGT Vision",
      subtitle: "Vision comes to life",
      backgroundColor: "#F5A623",
      category: "AGT PRO", // Cambiado a mayúsculas
      image: videoImage,
      modalContent: {
        title: "AGT Vision",
        description: `Enter a new dimension of visual storytelling with AGT Vision. Our context-aware generation system enhances your footage automatically for the perfect look.

        From color grading to complex effects, every tool is designed to elevate your story. With real-time demonstrations and customizable templates, your creative possibilities are endless.

        Welcome to the future of video creation, where imagination sets the only limits.`
      }
    },
    {
      title: "AGT Academic",
      subtitle: "The future of learning",
      backgroundColor: "#7ED321",
      category: "AGT EDU",
      image: academicImage,
      modalContent: {
        title: "AGT Academic",
        description: `Redefine scholarly research with AGT Academic. Seamlessly integrate cutting-edge databases with intelligent project planning tools.

        Our AI understands academic writing, helping generate accurate citations while maintaining scholarly integrity. Complex research becomes intuitive and manageable.

        From essays to dissertations, AGT Academic provides the tools you need at every academic stage.`
      }
    },
    {
      title: "AGT Growth",
      subtitle: "Learn at your own pace",
      backgroundColor: "#BD10E0",
      category: "AGT EDU",
      image: growthImage,
      modalContent: {
        title: "AGT Growth",
        description: `Experience personalized learning with AGT Growth. Our AI creates adaptive learning paths that evolve with your progress and goals.

        Watch your knowledge grow through visualized progress maps while the system optimizes your learning journey. Every interaction deepens understanding of your learning style.

        Welcome to education reimagined, where learning adapts to you.`
      }
    },
    {
      title: "AGT Product",
      subtitle: "Decisions that matter",
      backgroundColor: "#9013FE",
      category: "AGT UPS",
      image: productImage,
      modalContent: {
        title: "AGT Product",
        description: `Transform product management with AGT Product. Our AI analyzes market trends, customer behavior, and competitive landscapes for actionable insights.

        Optimize pricing strategies, analyze market positioning, and plan product development with confidence. Make informed decisions that drive real business growth.

        Step into the future of product management with data-driven excellence.`
      }
    },
    {
      title: "AGT Cash Flow",
      subtitle: "Effortless finance",
      backgroundColor: "#D0021B",
      category: "AGT UPS",
      image: cashFlowImage,
      modalContent: {
        title: "AGT Cash Flow",
        description: `Reimagine financial management with AGT Cash Flow. Our AI instantly processes and categorizes financial documents, updating your cash flow in real-time.

        Track expenses, forecast revenue, and plan investments with intelligent insights. The chat-based interface makes complex operations simple.

        Experience financial management where technology removes complexity.`
      }
    },
    {
      title: "AGT Council",
      subtitle: "Collective wisdom",
      backgroundColor: "#417505",
      category: "AGT UPS",
      image: councilImage,
      modalContent: {
        title: "AGT Council",
        description: `Access collective wisdom with AGT Council. Our AI simulates expert panels for comprehensive insights on your specific challenges.

        Get instant analysis and actionable recommendations for strategic decisions. Whether evaluating opportunities or assessing risks, receive context-aware guidance.

        Enter a new era of decision-making where expertise meets innovation.`
      }
    }
  ];

  return (
    <>
      <div className="w-full min-h-screen bg-[#f5f5f7] mt-10">
        <div className="max-w-[2560px] mx-auto grid grid-cols-1 gap-3 md:gap-3">
          <motion.section 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full bg-white"
          >
            <div className="relative">
              <HeroBackground />
            </div>
          </motion.section>

          <div className="w-full px-3">
            <div className="max-w-[2560px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-3">
              <motion.section 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="w-full bg-[#000000]"
              >
                <div className="relative h-[580px] w-full max-w-[1262px] bg-[#1a1a1a] overflow-hidden">

                <img
                    src={consciousAIImage}
                    alt="Conscious AI"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-between pb-12 pt-[52px] px-6">
                    <div className="text-center w-full">
                    <h2 className="text-[32px] sm:text-[48px] md:text-[56px] font-semibold text-white">AGT Ultra</h2>
                    <p className="text-[19px] sm:text-[24px] md:text-[28px] mt-[6px] text-white">Outperforming GPT o1, o3, Claude 3.7, and Grok 3.</p>
                      <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mt-6">
                        <ButtonStyle to="/ultra">Learn more</ButtonStyle>
                        <ButtonStyle to="https://agtapp.com/" variant="dark" isExternal>Try now</ButtonStyle>
                      </div>
                    </div>
                    <div>
                      <p className="text-[14px] sm:text-[15px] md:text-[17px] text-[#86868b] text-center">
                        Reasoning and understanding model
                      </p>
                    </div>
                  </div>
                </div>
              </motion.section>

              <motion.section 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="w-full bg-white"
              >
                <div className="relative h-[580px] w-full max-w-[1262px] bg-[#f5f5f7] overflow-hidden">
                  <img
                    src={agtUpsImage}
                    alt="AGT Ups"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-between pb-12 pt-[52px] px-6">
                    <div className="text-center w-full">
                      <h2 className="text-[32px] sm:text-[48px] md:text-[56px] font-semibold text-white">AGT Ups</h2>
                      <p className="text-[19px] sm:text-[24px] md:text-[28px] mt-[6px] text-white">Increase sales. Optimize finances. Maximize profits.</p>
                      <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mt-6">
                        <ButtonStyle to="/ups-docs">Learn more</ButtonStyle>
                        <ButtonStyle to="/ups-overview" variant="dark">Discover AGT Ups</ButtonStyle>
                      </div>
                    </div>
                    <div>
                      <p className="text-[14px] sm:text-[15px] md:text-[17px] text-[#6e6e73] text-center">
                        Power your business with AI
                      </p>
                    </div>
                  </div>
                </div>
              </motion.section>

              <motion.section 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="w-full bg-white"
              >
                <div className="relative h-[580px] w-full max-w-[1262px] bg-[#e5e5e7] overflow-hidden">
                  <img
                    src={agtEduImage}
                    alt="AGT Edu"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-between pb-12 pt-[52px] px-6">
                    <div className="text-center w-full">
                      <h2 className="text-[32px] sm:text-[48px] md:text-[56px] font-semibold text-white">AGT Edu</h2>
                      <p className="text-[19px] sm:text-[24px] md:text-[28px] mt-[6px] text-white">Better grades. Less effort. Stand out.</p>
                      <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mt-6">
                        <ButtonStyle to="/edu-docs">Learn more</ButtonStyle>
                        <ButtonStyle to="/edu" variant="dark">Discover AGT Edu</ButtonStyle>
                      </div>
                    </div>
                    <div>
                      <p className="text-[14px] sm:text-[15px] md:text-[17px] text-[#6e6e73] text-center">
                        The educational revolution is here
                      </p>
                    </div>
                  </div>
                </div>
              </motion.section>

              <motion.section 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="w-full bg-[#000000]"
              >
                <div className="relative h-[580px] w-full max-w-[1262px] bg-[#2d2d2d] overflow-hidden">
                  <img
                    src={purposeImage}
                    alt="Our Purpose"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-between pb-12 pt-[52px] px-6">
                    <div className="text-center w-full">
                      <h2 className="text-[32px] sm:text-[48px] md:text-[56px] font-semibold text-white">Our Purpose</h2>
                      <p className="text-[19px] sm:text-[24px] md:text-[28px] mt-[6px] text-white">Unifying human potential.</p>
                      <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mt-6">
                        <ButtonStyle to="/vision">Learn more</ButtonStyle>
                        <ButtonStyle to="/about" variant="dark">Our values</ButtonStyle>
                      </div>
                    </div>
                    <div>
                      <p className="text-[14px] sm:text-[15px] md:text-[17px] text-[#86868b] text-center">
                        Building tomorrow, today
                      </p>
                    </div>
                  </div>
                </div>
              </motion.section>
            </div>
          </div>

          <motion.section 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full bg-white"
          >
            <div className="relative h-[580px] sm:h-[692px] w-full max-w-[2560px] mx-auto bg-[#f0f0f2] overflow-hidden">
              <img
                src={allInOneImage}
                alt="All in One"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-between pb-12 pt-[52px] px-6">
                <div className="text-center">
                  <h2 className="text-[32px] sm:text-[48px] md:text-[56px] font-semibold text-white">All in One</h2>
                  <p className="text-[19px] sm:text-[24px] md:text-[28px] mt-[6px] text-white">Everything you need. United.</p>
                  <p className="text-[14px] sm:text-[15px] md:text-[17px] mt-[12px] text-[#BDBDBD]">One ecosystem. Infinite possibilities.</p>
                  <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-6">
                    <ButtonStyle to="/integration-guide">Explore</ButtonStyle>
                    <ButtonStyle to="/docs" variant="dark">Learn about ecosystem</ButtonStyle>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          <div className="w-full px-3">
            <div className="max-w-[2560px] mx-auto py-20">
              <h1 className="text-[28px] sm:text-[56px] md:text-[48px] font-semibold text-[#1d1d1f] mb-12 text-left tracking-[-0.28px] leading-[60px] pl-[max(calc((100vw-1440px)/2),24px)]">
                Explore the lineup.
              </h1>
              
              <div className="relative">
                <div 
                  ref={scrollContainerRef}
                  className="flex snap-x snap-mandatory space-x-8 overflow-x-auto scrollbar-hide pb-8"
                  style={{
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {productCards.map((card, index) => (
                    <motion.div
                      key={card.title}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.2 }}
                      className={`flex-none snap-center rounded-3xl text-white relative overflow-hidden w-[280px] md:w-[405px] h-[500px] md:h-[740px] group cursor-pointer ${
                        index === 0 ? 'ml-[max(calc((100vw-1440px)/2),24px)]' : ''
                      }`}
                      style={{
                        backgroundColor: card.backgroundColor,
                        scrollSnapAlign: 'center'
                      }}
                      onClick={() => {
                        setSelectedCard(card);
                        setIsModalOpen(true);
                      }}
                    >
                      <img
                        src={card.image}
                        alt={card.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent">
                        <div className="absolute top-3 sm:top-6 left-3 sm:left-6 right-3 sm:right-6 text-center">
                          <span className="text-[10px] sm:text-sm font-medium text-white/90">
                            {card.category}
                          </span>
                          <h3 className="text-base sm:text-2xl font-semibold text-white mt-1 sm:mt-2">
                            {card.title}
                          </h3>
                          <p className="text-xs sm:text-lg text-white/90 mt-0.5 sm:mt-1">
                            {card.subtitle}
                          </p>
                        </div>
                        <button className="absolute bottom-3 sm:bottom-6 right-3 sm:right-6 w-6 sm:w-10 h-6 sm:h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center transition-all group-hover:bg-white/20">
                          <Plus className="w-3 sm:w-6 h-3 sm:h-6 text-white" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                <div className="absolute right-4 sm:right-6 lg:right-8 -bottom-12 flex space-x-2">
                  <button
                    onClick={scrollLeft}
                    className="w-8 h-8 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center"
                    aria-label="Previous"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={scrollRight}
                    className="w-8 h-8 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center"
                    aria-label="Next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StoryModal 
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCard(null);
        }}
        content={selectedCard?.modalContent}
      />
    </>
  );
};

export default DirectiveAIHome;